import React from "react";
import { useParams } from "react-router-dom";

import AlertConfig from "features/Checks/AlertConfig";
import AlertDestinationDetail from "features/Checks/AlertDestinationDetail";
import AlertDestinationList from "features/Checks/AlertDestinationList";
import AlertRuleDetail from "features/Checks/AlertRuleDetail";
import AlertRuleList from "features/Checks/AlertRuleList";
import PageContent from "ui/PageContent";
import PageSecondaryNavigation, {
  PageNavLink,
} from "ui/PageSecondaryNavigation";

import CheckList from "features/Checks/CheckList";

import { useRoutes } from "utils/routes";
import { useLoadServerIfNeeded } from "utils/useLoadServerIfNeeded";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import Loading from "ui/Loading";

type Props = {
  tab: string;
};

const CheckUp: React.FunctionComponent<Props> = ({ tab }) => {
  // `slug` is absent on server/database routes, so read the org from context.
  const { slug: organizationSlug } = useCurrentOrganization();
  const {
    serverId: propsServerId,
    databaseId,
    destinationId,
    alertRuleId,
  } = useParams();
  const {
    organizationChecks,
    organizationAlerts,
    organizationAlertDestinations,
    organizationAlertRules,
    serverChecks,
    serverAlerts,
    serverAlertRules,
    serverAlertDestinations,
    databaseChecks,
  } = useRoutes();
  const { serverId, serverIdLoaded } = useLoadServerIfNeeded(
    propsServerId,
    databaseId,
  );

  if (!serverIdLoaded) {
    return <Loading />;
  }

  const featureNav = (
    <PageSecondaryNavigation>
      <PageNavLink
        to={
          databaseId
            ? databaseChecks(databaseId)
            : serverId
            ? serverChecks(serverId)
            : organizationChecks(organizationSlug)
        }
        end={false}
      >
        Summary Report
      </PageNavLink>
      <PageNavLink
        to={
          serverId
            ? serverAlerts(serverId)
            : organizationAlerts(organizationSlug)
        }
      >
        Configure
      </PageNavLink>
      <PageNavLink
        to={
          serverId
            ? serverAlertRules(serverId)
            : organizationAlertRules(organizationSlug)
        }
        end={false}
      >
        Alert Rules
      </PageNavLink>
      <PageNavLink
        to={
          serverId
            ? serverAlertDestinations(serverId)
            : organizationAlertDestinations(organizationSlug)
        }
        end={false}
      >
        Alert Destinations
      </PageNavLink>
    </PageSecondaryNavigation>
  );

  return (
    <PageContent
      title="Alerts &amp; Check-Up"
      pageCategory="checks"
      pageName="index"
      pageTab={tab}
      featureNav={featureNav}
    >
      {tab === "checks" && (
        <CheckList
          organizationSlug={organizationSlug}
          serverId={serverId}
          databaseId={databaseId}
        />
      )}
      {tab === "alerts" && <AlertConfig />}
      {tab === "destinations" &&
        (destinationId ? (
          <AlertDestinationDetail
            organizationSlug={organizationSlug}
            destinationId={destinationId}
          />
        ) : (
          <AlertDestinationList organizationSlug={organizationSlug} />
        ))}
      {tab === "rules" &&
        (alertRuleId ? (
          <AlertRuleDetail
            organizationSlug={organizationSlug}
            alertRuleId={alertRuleId}
            serverId={serverId}
          />
        ) : (
          <AlertRuleList
            organizationSlug={organizationSlug}
            serverId={serverId}
          />
        ))}
    </PageContent>
  );
};

export default CheckUp;
